import React, { useState } from 'react';
import { Badge, Button, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ncoder/ui';
import { ChevronDown, ChevronRight, FileCode, AlertTriangle, XCircle, AlertCircle, Info, ExternalLink, Clock } from 'lucide-react';
import { WeaveIcon } from '@/components/atoms/WeaveIcon';
import { cn } from '@/lib/utils';
import { openExternalUrl, safeWebUrl } from '@/lib/safeUrl';

// Individual finding within a metadata item
export interface FindingDetail {
  id: string;
  severity: string;
  rule: string;
  type: string;
  category: string;
  message: string;
  affected_file: string;
  line: number;
  column: number;
  engine: string;
  status: string;
  found_date: string;
  documentation_url?: string;
}

// Metadata item containing multiple findings
export interface MetadataItem {
  metadata_item_id: string;
  file_name: string;
  metadata_type: string;
  analysis_timestamp: string;
  findings_count: number;
  findings: FindingDetail[];
}

interface MetadataItemRowProps {
  item: MetadataItem;
  isExpanded?: boolean;
  onToggle?: (id: string) => void;
  onImproveWithAI?: (item: MetadataItem) => void;
  isScanning?: boolean;
}

// Helper functions
const getMetadataTypeIcon = (type: string) => {
  // Different icons for different metadata types
  const icons: Record<string, React.ReactNode> = {
    'ApexClass': <FileCode className="h-4 w-4 text-purple-500" />,
    'ApexTrigger': <FileCode className="h-4 w-4 text-orange-500" />,
    'LightningComponentBundle': <FileCode className="h-4 w-4 text-yellow-500" />,
    'Flow': <FileCode className="h-4 w-4 text-green-500" />,
    'CustomObject': <FileCode className="h-4 w-4 text-blue-500" />,
  };
  return icons[type] || <FileCode className="h-4 w-4 text-muted-foreground" />;
};

// Get the highest severity in findings
const getHighestSeverity = (findings: FindingDetail[]): string => {
  const severityOrder = ['Critical', 'High', 'Medium', 'Low'];
  for (const severity of severityOrder) {
    if (findings.some(f => f.severity === severity)) {
      return severity;
    }
  }
  return 'Low';
};

// Get severity dot color
const getSeverityDotColor = (severity: string): string => {
  const colors: Record<string, string> = {
    'Critical': 'bg-red-500',
    'High': 'bg-orange-500',
    'Medium': 'bg-yellow-500',
    'Low': 'bg-blue-500',
  };
  return colors[severity] || 'bg-gray-400';
};

const getSeverityInfo = (severity: string) => {
  const info: Record<string, { color: string; bgColor: string; icon: React.ReactNode }> = {
    'Critical': { 
      color: 'text-red-600 dark:text-red-400', 
      bgColor: 'bg-red-100 dark:bg-red-900/30',
      icon: <XCircle className="h-4 w-4" />
    },
    'High': { 
      color: 'text-red-500 dark:text-red-400', 
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      icon: <AlertTriangle className="h-4 w-4" />
    },
    'Medium': { 
      color: 'text-yellow-600 dark:text-yellow-400', 
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      icon: <AlertCircle className="h-4 w-4" />
    },
    'Low': { 
      color: 'text-blue-600 dark:text-blue-400', 
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      icon: <Info className="h-4 w-4" />
    },
  };
  return info[severity] || { color: 'text-gray-500', bgColor: 'bg-gray-50', icon: <Info className="h-4 w-4" /> };
};

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

const formatRelativeTime = (dateString: string) => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  } catch {
    return 'Unknown';
  }
};

// Count findings by severity
const countBySeverity = (findings: FindingDetail[]) => {
  return findings.reduce((acc, f) => {
    acc[f.severity] = (acc[f.severity] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
};

export const MetadataItemRow: React.FC<MetadataItemRowProps> = ({
  item,
  isExpanded: controlledExpanded,
  onToggle,
  onImproveWithAI,
  isScanning,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isExpanded = controlledExpanded ?? internalExpanded;
  
  const handleToggle = () => {
    if (onToggle) {
      onToggle(item.metadata_item_id);
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const severityCounts = countBySeverity(item.findings);
  
  return (
    <Collapsible open={isExpanded} onOpenChange={handleToggle}>
      {/* Level 1: Metadata Item Header */}
      <CollapsibleTrigger asChild>
        <div 
          className={cn(
            "flex items-center gap-3 p-3 cursor-pointer transition-colors",
            "hover:bg-muted/50 border-b",
            isExpanded && "bg-muted/30"
          )}
        >
          {/* Expand/Collapse Icon */}
          <div className="flex-shrink-0">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>

          {/* File Icon */}
          <div className="flex-shrink-0">
            {getMetadataTypeIcon(item.metadata_type)}
          </div>

          {/* File Name & Metadata Type */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">{item.file_name}</span>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground">{item.metadata_type}</span>
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <Clock className="h-3 w-3" />
              <span>{formatRelativeTime(item.analysis_timestamp)}</span>
            </div>
          </div>

          {/* Compact Severity Summary */}
          <div className="flex-shrink-0 flex items-center gap-3 text-xs text-muted-foreground">
            {severityCounts['Critical'] && (
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-red-600" />
                <span>{severityCounts['Critical']}</span>
              </span>
            )}
            {severityCounts['High'] && (
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-orange-500" />
                <span>{severityCounts['High']}</span>
              </span>
            )}
            {severityCounts['Medium'] && (
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-yellow-500" />
                <span>{severityCounts['Medium']}</span>
              </span>
            )}
            {severityCounts['Low'] && (
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-blue-500" />
                <span>{severityCounts['Low']}</span>
              </span>
            )}
          </div>

          {/* Total Issues Count */}
          <div className="flex-shrink-0">
            <Badge variant="secondary" className="text-xs font-normal">
              {item.findings_count} {item.findings_count === 1 ? 'issue' : 'issues'}
            </Badge>
          </div>

          {/* Improve with AI */}
          {onImproveWithAI && (
            <div className="flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                disabled={isScanning}
                onClick={(e) => {
                  e.stopPropagation();
                  onImproveWithAI(item);
                }}
              >
                <WeaveIcon className="h-3 w-3 mr-1" />
                Let's Weave
              </Button>
            </div>
          )}
        </div>
      </CollapsibleTrigger>

      {/* Level 2: Nested Findings */}
      <CollapsibleContent>
        <div className="border-b bg-muted/10">
          {item.findings.map((finding, index) => (
            <FindingDetailRow 
              key={finding.id} 
              finding={finding} 
              isLast={index === item.findings.length - 1}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Individual Finding Detail Row (Level 2)
interface FindingDetailRowProps {
  finding: FindingDetail;
  isLast?: boolean;
}

const FindingDetailRow: React.FC<FindingDetailRowProps> = ({ finding, isLast }) => {
  const severityInfo = getSeverityInfo(finding.severity);

  return (
    <div 
      className={cn(
        "flex items-start gap-4 py-3 px-4 pl-14 transition-colors hover:bg-muted/30",
        !isLast && "border-b border-dashed border-muted"
      )}
    >
      {/* Severity Icon & Badge */}
      <div className="flex-shrink-0 flex items-center gap-2">
        <div className={cn("p-1 rounded", severityInfo.bgColor, severityInfo.color)}>
          {severityInfo.icon}
        </div>
        <Badge 
          variant="outline" 
          className={cn("text-xs", severityInfo.color)}
        >
          {finding.severity}
        </Badge>
      </div>

      {/* Finding Details */}
      <div className="flex-1 min-w-0 space-y-1">
        {/* Rule & Message */}
        <div>
          <span className="font-medium text-sm">{finding.rule}</span>
          <span className="text-muted-foreground text-sm mx-2">•</span>
          <span className="text-sm text-muted-foreground">{finding.message}</span>
        </div>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Badge variant="outline" className="text-xs px-1.5 py-0">
              {finding.type}
            </Badge>
          </span>
          <span className="flex items-center gap-1">
            Category: <span className="font-medium">{finding.category}</span>
          </span>
          <span className="flex items-center gap-1">
            Line: <span className="font-medium">{finding.line}</span>, Col: <span className="font-medium">{finding.column}</span>
          </span>
          <span className="flex items-center gap-1">
            Engine: <span className="font-medium">{finding.engine}</span>
          </span>
          <Badge 
            variant={finding.status === 'Open' ? 'destructive' : 'secondary'} 
            className="text-xs px-1.5 py-0"
          >
            {finding.status}
          </Badge>
        </div>
      </div>

      {/* Documentation Link */}
      {safeWebUrl(finding.documentation_url) && (
        <div className="flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              openExternalUrl(finding.documentation_url);
            }}
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            Docs
          </Button>
        </div>
      )}
    </div>
  );
};

export default MetadataItemRow;
